import { AlertTriangle } from 'lucide-react';
import { useCompanyContext } from '../context/CompanyContext';
import { useLang } from '../context/LangContext';
import { t } from '../i18n/dictionary';

// Set VITE_WHATSAPP_NUMBER in .env (e.g. 51987654321 — country code + number, no +)
const WA_NUMBER = import.meta.env.VITE_WHATSAPP_NUMBER ?? '51999999999';

export type TrialBannerTier = 'hidden' | 'expired' | 'expiring_today' | 'expiring_tomorrow' | 'countdown';

/**
 * Fix 2026-09-18: TrialBanner leía trialEndsAt/isPaid de UserRoleContext
 * (tabla profiles), una fuente completamente separada de subscriptions —
 * la que realmente controla el acceso vía TrialGate en App.tsx. Podían
 * desincronizarse: el banner decía "vencido" mientras el gate real seguía
 * dejando pasar todo, o viceversa. Ahora ambos leen de subscriptions.
 * Pura y exportada para poder testear la selección de tier sin renderizar
 * el componente — ver TrialBanner.test.ts.
 */
export function deriveTrialBannerTier(
  subscriptionLoading: boolean,
  subscriptionStatus: string | undefined,
  isTrialExpired: boolean,
  daysRemaining: number,
): TrialBannerTier {
  if (subscriptionLoading) return 'hidden';
  if (subscriptionStatus === 'active') return 'hidden';
  if (isTrialExpired) return 'expired';
  if (daysRemaining === 0) return 'expiring_today';
  if (daysRemaining === 1) return 'expiring_tomorrow';
  return 'countdown';
}

export default function TrialBanner() {
  const { lang } = useLang();
  const { subscription, subscriptionLoading, isTrialExpired, daysRemaining } = useCompanyContext();

  const tier = deriveTrialBannerTier(subscriptionLoading, subscription?.status, isTrialExpired, daysRemaining);

  if (tier === 'hidden') return null;

  if (tier === 'expired') {
    return (
      <div
        style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--surface-raised)' }}
        className="flex items-center justify-between gap-4 px-6 py-2 text-sm text-[var(--negative)]"
      >
        <span>
          {t('trial.expired', lang)}
        </span>
        <a
          href={`https://wa.me/${WA_NUMBER}`}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 rounded-lg bg-green-600 px-3 py-1 text-xs font-bold text-white hover:bg-green-500 transition-colors"
        >
          💬 WhatsApp
        </a>
      </div>
    );
  }

  if (tier === 'expiring_today' || tier === 'expiring_tomorrow') {
    return (
      <div
        style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--surface-raised)' }}
        className="flex items-center gap-2 px-6 py-2 text-sm text-[var(--warning)]"
      >
        <span className="flex items-center gap-1.5">
          <AlertTriangle size={14} className="icon-warn shrink-0" />
          {tier === 'expiring_today' ? t('trial.expiresToday', lang) : t('trial.expiresTomorrow', lang)}
        </span>
        <a
          href={`https://wa.me/${WA_NUMBER}`}
          target="_blank"
          rel="noreferrer"
          className="ml-2 shrink-0 rounded-lg bg-green-600 px-3 py-1 text-xs font-bold text-white hover:bg-green-500 transition-colors"
        >
          💬 Contactar
        </a>
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'var(--brand-soft)',
        borderBottom: '1px solid var(--brand-soft)',
        color: 'var(--brand)',
      }}
      className="flex items-center gap-2 px-6 py-2 text-sm min-w-0"
    >
      <span className="text-xs sm:text-sm truncate">
        🎯 Período de prueba:{' '}
        <strong style={{ color: 'var(--brand)' }}>{daysRemaining} días restantes</strong>
        {' '}— Explora todas las funciones de TryQuiPus
      </span>
    </div>
  );
}
